/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * iOS / iPhone heuristics — Feb 8, 2026.
 *
 * Used by the upload pipeline to:
 *   • Detect when the user is on an iPhone (Photos picker is the
 *     primary source of iCloud-fetch silent failures).
 *   • Surface a "use Files > On My iPhone" advisory for very large
 *     videos where Photos picker reliability drops.
 *   • Sanity-check a freshly picked File for zero-byte / dead-handle
 *     conditions BEFORE we start the full upload pipeline so the
 *     user never sees a silent reset to the upload zone.
 *
 * Pure browser-side. No window access at module load — every helper
 * guards via `typeof window` so this file is import-safe in tests.
 */

// Threshold above which we recommend Files > On My iPhone.
// Picked Photos-app exports tend to fail more often as size grows,
// especially when the original lives in iCloud (must download first).
export const IPHONE_PHOTOS_FILES_HINT_BYTES = 500 * 1024 * 1024; // 500 MB

// Hard cap for the readability pre-flight. iCloud download in the
// background can pause the JS thread for several seconds — but a
// genuinely-readable handle responds in well under 8 s.
export const FILE_READ_PROBE_TIMEOUT_MS = 8000;


export function isIphoneLikely() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Genuine iPhone Safari + iPadOS-pretending-to-be-Mac (handles
  // recent iPad masquerading) + iPod Touch.
  if (/iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Mac with maxTouchPoints > 1.
  if (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}


/**
 * `<input>` elements have an underlying `accept` attribute we control,
 * but the user ultimately picks Photos vs Files inside iOS — we can't
 * detect WHICH source they used directly. Best signal is the file's
 * `webkitRelativePath` (always empty for Photos-picker) plus the
 * common Photos export filename pattern (e.g. IMG_xxxx.MOV).
 */
export function isLikelyFromPhotosPicker(file) {
  if (!file) return false;
  if (file.webkitRelativePath) return false;
  const name = (file.name || "").toUpperCase();
  return /^IMG_\d+\.(MOV|MP4|HEIC|HEIF)$/i.test(name);
}


/**
 * Light-weight, non-throwing handle probe. Reads the FIRST and LAST
 * 1 KiB of the file to confirm:
 *
 *   • size > 0 (catches the "iCloud fetch never completed" case
 *     where Photos returns a 0-byte File handle).
 *   • The file is actually readable (catches dead handles after the
 *     Photos picker closes — sometimes the OS releases the
 *     temporary export before our JS gets a chance to start the
 *     upload).
 *
 * Returns a structured result so the caller can show targeted UX:
 *   { ok: true } — file is fine, proceed.
 *   { ok: false, reason: "empty_file" }
 *   { ok: false, reason: "unreadable", detail: "..." }
 *   { ok: false, reason: "timeout" }
 *
 * Never throws. Always resolves within FILE_READ_PROBE_TIMEOUT_MS.
 */
export async function probeFileReadability(file) {
  if (!file) return { ok: false, reason: "no_file" };
  if (typeof file.size !== "number" || file.size <= 0) {
    return { ok: false, reason: "empty_file" };
  }
  // Probe a 1 KiB head + 1 KiB tail. We don't need to read the whole
  // thing — we just need confirmation that the OS will hand the bytes
  // over when chunkedUpload starts slicing.
  const probeSize = Math.min(1024, file.size);
  const tailStart = Math.max(0, file.size - probeSize);

  try {
    const result = await Promise.race([
      (async () => {
        const head = file.slice(0, probeSize);
        const tail = file.slice(tailStart, file.size);
        const headBuf = await head.arrayBuffer();
        const tailBuf = await tail.arrayBuffer();
        if (!headBuf || headBuf.byteLength === 0) {
          return { ok: false, reason: "unreadable", detail: "head_empty" };
        }
        if (!tailBuf || tailBuf.byteLength === 0) {
          return { ok: false, reason: "unreadable", detail: "tail_empty" };
        }
        return { ok: true };
      })(),
      new Promise((resolve) => setTimeout(
        () => resolve({ ok: false, reason: "timeout" }),
        FILE_READ_PROBE_TIMEOUT_MS,
      )),
    ]);
    return result;
  } catch (e) {
    return {
      ok: false,
      reason: "unreadable",
      detail: (e && e.message) ? String(e.message).slice(0, 200) : "exception",
    };
  }
}


/**
 * Should we surface a "Tip: pick from Files > On My iPhone for very
 * large videos" advisory? Returns true when the device is iPhone-ish
 * AND the file looks like a Photos-picker export AND the size is
 * over the threshold.
 */
export function shouldSuggestFilesPicker(file) {
  if (!isIphoneLikely()) return false;
  if (!file) return false;
  if (typeof file.size !== "number" || file.size <= 0) return false;
  if (file.size < IPHONE_PHOTOS_FILES_HINT_BYTES) return false;
  return isLikelyFromPhotosPicker(file);
}


/**
 * Structured logger. Always uses `console.info` so the output is
 * grep-able from a remote browser console with the standard
 * `[lr-upload]` prefix the operator already monitors.
 *
 * vocabulary table:
 *   "picker.file_selected"
 *   "picker.no_file_returned"
 *   "picker.empty_file"
 *   "picker.unreadable_file"
 *   "picker.duration_probe_started"
 *   "picker.duration_probe_done"
 *   "picker.iphone_files_hint_shown"
 *   "upload.started"
 *   "upload.interrupted"
 *   "upload.reset_reason"
 *   "lifecycle.visibilitychange"
 *   "lifecycle.pagehide"
 */
export function logUploadEvent(event, payload = {}) {
  if (typeof console === "undefined" || !console.info) return;
  try {
    const safe = {};
    for (const k of Object.keys(payload || {})) {
      const v = payload[k];
      if (v === undefined) continue;
      // Don't leak File objects into console (memory pressure).
      if (v && typeof v === "object" && "size" in v && "name" in v) {
        safe[k] = `<File name=${v.name} size=${v.size} type=${v.type || ""}>`;
      } else {
        safe[k] = v;
      }
    }
    console.info(`[lr-upload] ${event}`, safe);
  } catch {
    /* logging must never throw */
  }
}
